import { useState, useCallback, type FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth as authApi, ApiError } from "../services/api";
import { useAuth } from "../hooks/useAuth";

type Step = "email" | "otp";

export default function LoginPage() {
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // If already logged in, redirect to where they came from (or dashboard)
  const from = (location.state as { from?: { pathname: string } })?.from
    ?.pathname ?? "/dashboard";

  if (isAuthenticated) {
    navigate(from, { replace: true });
    return null;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4">
      <div className="w-full max-w-md">
        {/* Branding */}
        <div className="mb-8 text-center">
          <img
            src="/zebra-z.png"
            alt="Zebra Renewables"
            className="mx-auto mb-6 h-24 w-auto"
          />
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">
            <span className="text-emerald-700">ZEBRA</span>{" "}
            <span className="text-gray-500 font-normal">RENEWABLES</span>
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            Invoice Verification Portal
          </p>
        </div>

        {/* Card */}
        <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
          {step === "email" ? (
            <EmailStep
              email={email}
              setEmail={setEmail}
              error={error}
              setError={setError}
              isSubmitting={isSubmitting}
              setIsSubmitting={setIsSubmitting}
              onSuccess={() => {
                setStep("otp");
                setError("");
              }}
            />
          ) : (
            <OtpStep
              email={email}
              otp={otp}
              setOtp={setOtp}
              error={error}
              setError={setError}
              isSubmitting={isSubmitting}
              setIsSubmitting={setIsSubmitting}
              onBack={() => {
                setStep("email");
                setOtp("");
                setError("");
              }}
              onSuccess={(token, user) => {
                login(token, user);
                navigate(from, { replace: true });
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Email Step ─────────────────────────────────────────────────────────────

function EmailStep({
  email,
  setEmail,
  error,
  setError,
  isSubmitting,
  setIsSubmitting,
  onSuccess,
}: {
  email: string;
  setEmail: (v: string) => void;
  error: string;
  setError: (v: string) => void;
  isSubmitting: boolean;
  setIsSubmitting: (v: boolean) => void;
  onSuccess: () => void;
}) {
  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError("");

      const trimmed = email.trim().toLowerCase();
      if (!trimmed) {
        setError("Email is required.");
        return;
      }

      if (!trimmed.endsWith("@zebrarenewables.com")) {
        setError("Only @zebrarenewables.com email addresses are allowed.");
        return;
      }

      setIsSubmitting(true);
      try {
        await authApi.requestOtp(trimmed);
        onSuccess();
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.message);
        } else {
          setError("Something went wrong. Please try again.");
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, setError, setIsSubmitting, onSuccess],
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Sign in</h2>
        <p className="mt-1 text-sm text-gray-500">
          Enter your Zebra Renewables email to receive a verification code.
        </p>
      </div>

      <div>
        <label
          htmlFor="email"
          className="block text-sm font-medium text-gray-700"
        >
          Email address
        </label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          autoFocus
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@zebrarenewables.com"
          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm placeholder:text-gray-400 focus:border-emerald-600 focus:outline-none focus:ring-1 focus:ring-emerald-600"
        />
      </div>

      {error && (
        <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="flex w-full items-center justify-center rounded-lg bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isSubmitting ? (
          <>
            <Spinner />
            Sending code...
          </>
        ) : (
          "Send Code"
        )}
      </button>
    </form>
  );
}

// ─── OTP Step ───────────────────────────────────────────────────────────────

function OtpStep({
  email,
  otp,
  setOtp,
  error,
  setError,
  isSubmitting,
  setIsSubmitting,
  onBack,
  onSuccess,
}: {
  email: string;
  otp: string;
  setOtp: (v: string) => void;
  error: string;
  setError: (v: string) => void;
  isSubmitting: boolean;
  setIsSubmitting: (v: boolean) => void;
  onBack: () => void;
  onSuccess: (token: string, user: import("../types").User) => void;
}) {
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState("");

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError("");

      const trimmedOtp = otp.trim();
      if (!trimmedOtp) {
        setError("Please enter the 6-digit code.");
        return;
      }

      if (!/^\d{6}$/.test(trimmedOtp)) {
        setError("Code must be exactly 6 digits.");
        return;
      }

      setIsSubmitting(true);
      try {
        const { token, user } = await authApi.verifyOtp(
          email.trim().toLowerCase(),
          trimmedOtp,
        );
        onSuccess(token, user);
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.message);
        } else {
          setError("Verification failed. Please try again.");
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, otp, setError, setIsSubmitting, onSuccess],
  );

  const handleResend = useCallback(async () => {
    setResending(true);
    setResendMessage("");
    setError("");
    try {
      await authApi.requestOtp(email.trim().toLowerCase());
      setResendMessage("A new code has been sent to your email.");
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to resend code. Please try again.");
      }
    } finally {
      setResending(false);
    }
  }, [email, setError]);

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">
          Enter verification code
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          We sent a 6-digit code to{" "}
          <span className="font-medium text-gray-700">{email}</span>.
        </p>
      </div>

      <div>
        <label
          htmlFor="otp"
          className="block text-sm font-medium text-gray-700"
        >
          Verification code
        </label>
        <input
          id="otp"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          required
          maxLength={6}
          value={otp}
          onChange={(e) => {
            // Only allow digits
            const val = e.target.value.replace(/\D/g, "");
            setOtp(val);
          }}
          placeholder="000000"
          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-center text-lg font-mono tracking-widest shadow-sm placeholder:text-gray-400 focus:border-emerald-600 focus:outline-none focus:ring-1 focus:ring-emerald-600"
        />
      </div>

      {error && (
        <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      )}

      {resendMessage && (
        <p className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">
          {resendMessage}
        </p>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="flex w-full items-center justify-center rounded-lg bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isSubmitting ? (
          <>
            <Spinner />
            Verifying...
          </>
        ) : (
          "Verify"
        )}
      </button>

      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={onBack}
          className="font-medium text-gray-600 hover:text-gray-900"
        >
          Use a different email
        </button>
        <button
          type="button"
          onClick={handleResend}
          disabled={resending}
          className="font-medium text-emerald-700 hover:text-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {resending ? "Resending..." : "Resend code"}
        </button>
      </div>
    </form>
  );
}

// ─── Shared Components ──────────────────────────────────────────────────────

function Spinner() {
  return (
    <svg
      className="mr-2 h-4 w-4 animate-spin"
      viewBox="0 0 24 24"
      fill="none"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}
